<script setup lang="ts">
import type { InternalAxiosRequestConfig } from "axios";
import { storeToRefs } from "pinia";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useId,
  useTemplateRef,
  watch,
} from "vue";
import { useRoute } from "vue-router";

import AiText from "@/components/AiText.vue";
import {
  assistantOpenRequest,
  getAssistantPageContext,
} from "@/components/assistantPageContext";
import AutoGrowTextarea from "@/components/AutoGrowTextarea.vue";
import Callout from "@/components/Callout.vue";
import Disclosure from "@/components/Disclosure.vue";
import EmptyState from "@/components/EmptyState.vue";
import IconButton from "@/components/IconButton.vue";
import LoadingButton from "@/components/LoadingButton.vue";
import { hasOpenModal, lockBodyScroll, trapTabKey } from "@/components/overlay";
import UiIcon from "@/components/UiIcon.vue";
import UiSwitch from "@/components/UiSwitch.vue";
import { useAuthStore } from "@/stores/auth";
import {
  type ApiResponse,
  isLlmInputRejectedFailure,
  isUnconfirmedOutcome,
  llmFailureMessage,
  postApi,
  retryAfterSeconds,
  validationFailureText,
} from "@/utils/apis";
import { toastError, toastInfo, toastUndo } from "@/utils/toaster";

/**
 * 어느 화면에서든 여는 AI 도우미(고객사 대표 요청 — 화면마다 프롬프트를 입력할 곳).
 * - 런처 자리(`placement`, 71차 UXG-02): `fab`(기본)은 우하단 원형 버튼. `topbar` 는 넓은 폭(≥761)에서 놓인 자리(상단바
 *   `.topbar-actions`)에 「AI 도우미」 버튼으로 서고 패널은 상단바 아래 오른쪽에 뜬다 — 우하단 버튼이 오른쪽 정렬 액션(primary ·
 *   행 편집 · 하단 버튼 줄)을 가렸다. 좁은 폭(≤760)은 `topbar` 여도 우하단 버튼이다(`#app` 으로 옮겨 그린다).
 * - 데스크톱은 비모달 패널(본문과 함께 조작 — 배경막 · 포커스 가둠 · 스크롤 잠금 없음). ESC 는 패널 안에서만 최소화한다.
 *   상단바 버튼은 패널이 열려 있는 동안에도 보이며 다시 누르면 최소화한다(`aria-expanded`).
 *   모바일(≤760)은 화면을 덮으므로 모달로 다룬다(`aria-modal` · Tab 가둠 · 본문 스크롤 잠금, UXD-02).
 * - 최소화해도 대화는 남고, 최소화 중 답이 오면 버튼에 표시를 단다. 워크스페이스가 바뀌면 대화 · 입력칸을 비운다(다른 회사 맥락이 섞이지 않게).
 * - 대화는 저장하지 않는다 — 지원자 정보가 섞일 수 있어 새로고침 · 로그아웃 뒤 남기지 않는다.
 * - 라우트가 바뀌면 진행 중 요청이 일괄 취소되므로(`router.ts`) 답을 기다리다 화면을 옮기면 다시 시도를 안내한다.
 *   「기다리지 않기」 · 대화 지우기 · 회사 전환도 요청을 실제로 끊는다(`ApiOptions.signal`, 71차 CR71-03).
 * - 화면이 등록한 "지금 작성한 내용" 요약(`assistantPageContext`)이 있으면 질문마다 함께 보낸다. 입력칸 위 칩으로 끌 수 있다(패널 상태, 기본 켬).
 * - 답은 `AiText` 로 그린다(문단 · 목록 · 굵게만, `v-html` 금지 — 모델 출력을 마크업으로 해석하지 않는다). 복사는 원문 그대로.
 * - 하단 고정 버튼 줄(StickyActionBar)이 있으면 그 줄이 `:root` 에 적는 `--ui-action-bar-offset` 만큼 위로 비켜서고,
 *   비모달 시트(SidePanel)가 오른쪽에 열려 있으면 `--ui-dock-right` 만큼 왼쪽으로 비켜선다(UXD-07 · UXE-15).
 */

const props = withDefaults(
  defineProps<{
    /** 런처 자리. `topbar` 는 넓은 폭(≥761)에서만 놓인 자리에 서고 좁은 폭은 우하단 버튼이다. */
    placement?: "fab" | "topbar";
  }>(),
  { placement: "fab" },
);

interface AssistantMessage {
  role: "user" | "assistant";
  content: string;
}

interface AssistantAskRequest {
  /** 서버 검증 `^[a-z0-9-]+$` — 빈 문자열은 422 라 없으면 뺀다. */
  pageId?: string;
  pageTitle?: string;
  /** 화면에 작성 중인 내용 요약(서버 상한 8000자). 없으면 뺀다. */
  pageContext?: string;
  messages: AssistantMessage[];
}

interface AssistantAskResponse {
  answer: string;
}

/** 패널 안 안내(오류 · 상한 · 기다리지 않기). tone 은 뜻으로 고른다 — 잠깐 기다림 = warn, 실패 = danger, 사용자가 멈춤 = neutral. */
interface AssistantNotice {
  message: string;
  retryable: boolean;
  tone: "neutral" | "warn" | "danger";
  /** 분당 상한 — 이 시각(ms)까지 「다시 시도」 를 잠그고 남은 초를 센다. */
  retryAt?: number;
  /** 남은 초로 문구를 만든다(분당 상한 카운트다운). */
  countdown?: (seconds: number) => string;
}

/** 질문 최대 글자 수(서버 상한과 같다) */
const MAX_LENGTH = 4000;
/** 서버로 보내는 최근 메시지 수 */
const HISTORY_LIMIT = 20;
/** 화면 제목 최대 글자 수(서버 상한과 같다) */
const PAGE_TITLE_LIMIT = 100;
/** 질문 422 의 칸 이름(서버 `AssistantAskRequest` 필드 — `validationFailureText`) */
const VALIDATION_FIELD_LABELS: Record<string, string> = {
  pageId: "화면 정보",
  pageTitle: "화면 제목",
  pageContext: "화면 내용",
  messages: "대화 기록",
  "messages.*.content": "대화 메시지",
  "messages.*.role": "대화 메시지",
};
/** 「보내는 내용 보기」 에 보이는 앞부분 글자 수 */
const CONTEXT_PREVIEW_LENGTH = 300;
/** 대기 말풍선에 경과 초를 붙이기 시작하는 초(LoadingButton `showElapsed` 와 같다) */
const ELAPSED_FROM_SECONDS = 10;
/** 보내자마자 같은 자리에 나타난 「기다리지 않기」 를 겹 누름으로 누르지 않게 무시하는 시간(ms) */
const STOP_GUARD_MS = 800;
/** 맨 아래로 본다고 칠 여유(px) — 이 안이면 새 답이 와도 따라 내려간다. */
const NEAR_BOTTOM_PX = 48;
/** 하루 상한 초과 뒤 전송 잠금 이유(보이는 한 줄) */
const DAILY_BLOCKED_REASON =
  "오늘 질문 상한을 모두 썼습니다. 내일 다시 이용해 주세요.";
/** 처음 쓰는 사용자에게 런처를 글자 알약으로 보인 뒤, 한 번 열면(세션 안) 원으로 접는다(우하단 버튼만). */
const LAUNCHER_SEEN_KEY = "selechub.aiAssistant.opened";

/** 마지막 답 아래 후속 질문 칩. 칩은 짧게, 보내는 질문은 문장으로. */
const FOLLOW_UPS = [
  { label: "더 짧게", question: "방금 답변을 더 짧게 정리해 주세요." },
  { label: "예시 하나 더", question: "예시를 하나 더 보여 주세요." },
] as const;

const route = useRoute();
const { workspace } = storeToRefs(useAuthStore());

const panelId = useId();
const titleId = useId();
const launcher = useTemplateRef<HTMLButtonElement>("launcher");
const panel = useTemplateRef<HTMLElement>("panel");
const input = useTemplateRef<InstanceType<typeof AutoGrowTextarea>>("input");
const log = useTemplateRef<HTMLElement>("log");

const open = ref(false);
const draft = ref("");
const messages = ref<AssistantMessage[]>([]);
const loading = ref(false);
const error = ref<AssistantNotice | null>(null);
/** 최소화 중 도착한 답 */
const unread = ref(false);
/** 하루 상한 초과 — 대화 지우기 · 워크스페이스 전환 전까지 전송을 잠근다(다시 보내도 같은 429). */
const dailyLimited = ref(false);
/** 위로 스크롤해 읽는 중에 도착한 답 — 끌어내리지 않고 「새 답변」 알약을 띄운다. */
const newAnswerBelow = ref(false);
/** 방금 복사한 답(2초 동안 「복사됨」) */
const copiedIndex = ref<number | null>(null);
/** 모바일 모달에서 지운 대화 — 패널 안 「되돌리기」 줄이 들고 있다. */
const clearedMessages = ref<AssistantMessage[] | null>(null);
/** 대화를 비우거나 기다리지 않기로 하면 올린다 — 그 전에 보낸 요청의 답은 버린다. */
let epoch = 0;
/** 진행 중 질문 요청을 끊는 controller(답을 버릴 때 요청도 끊는다). */
let askController: AbortController | null = null;
let loadingStartedAt = 0;
let copiedTimer: ReturnType<typeof setTimeout> | undefined;

const pageId = computed(() => route.meta["pageId"] ?? "");
const pageTitle = computed(() => route.meta["title"] ?? "셀렉허브");

/** 지금 화면이 등록한 작성 내용(등록 화면 · 화면 상태가 바뀌면 따라 바뀐다). 없으면 null. */
const pageContextText = computed(() => getAssistantPageContext());
const pageContextAvailable = computed(() => pageContextText.value !== null);
/** 입력칸 위 "작성 내용 포함" 칩. 대화와 같은 수명이라 저장하지 않는다. */
const sharePageContext = ref(true);
const contextPreviewOpen = ref(false);
const pageContextLength = computed(() =>
  pageContextText.value === null ? 0 : Array.from(pageContextText.value).length,
);
const pageContextPreview = computed(() => {
  const chars = Array.from(pageContextText.value ?? "");
  return chars.length > CONTEXT_PREVIEW_LENGTH
    ? `${chars.slice(0, CONTEXT_PREVIEW_LENGTH).join("")}…`
    : chars.join("");
});

// 작성 내용 검토는 화면의 「AI 검토」 가 맡는다(UXD-28) — 도우미 첫 칩은 화면 사용법 질문으로 둔다.
const suggestions = computed(() => [
  "이 화면에서 무엇을 하면 되나요?",
  "입력 예시를 보여 주세요",
  `${pageTitle.value} 다음에는 어떤 단계로 넘어가나요?`,
]);

const launcherLabel = computed(() =>
  unread.value ? "AI 도우미 — 새 답변이 있습니다" : "AI 도우미",
);

function readLauncherSeen(): boolean {
  try {
    return sessionStorage.getItem(LAUNCHER_SEEN_KEY) === "1";
  } catch {
    return false;
  }
}

const launcherSeen = ref(readLauncherSeen());

const canSend = computed(
  () => draft.value.trim() !== "" && !loading.value && !dailyLimited.value,
);
const sendBlockedReason = computed(() =>
  dailyLimited.value && !loading.value ? DAILY_BLOCKED_REASON : undefined,
);
const showFollowUps = computed(
  () =>
    !loading.value &&
    !error.value &&
    !dailyLimited.value &&
    messages.value.at(-1)?.role === "assistant",
);

// ---- 초 세기(대기 경과 · 분당 상한 카운트다운) — 필요한 동안만 1초 간격으로 돈다 ----
const now = ref(Date.now());
let ticker: ReturnType<typeof setInterval> | undefined;

const pendingSeconds = computed(() =>
  Math.max(0, Math.floor((now.value - loadingStartedAt) / 1000)),
);
const retryRemaining = computed(() => {
  const retryAt = error.value?.retryAt;
  return retryAt === undefined
    ? 0
    : Math.max(0, Math.ceil((retryAt - now.value) / 1000));
});
const noticeMessage = computed(() => {
  const notice = error.value;
  if (!notice) return "";
  if (notice.countdown && notice.retryAt !== undefined) {
    return retryRemaining.value > 0
      ? notice.countdown(retryRemaining.value)
      : "이제 다시 질문할 수 있습니다.";
  }
  return notice.message;
});
const needsTicker = computed(() => loading.value || retryRemaining.value > 0);

function stopTicker(): void {
  if (ticker !== undefined) clearInterval(ticker);
  ticker = undefined;
}

watch(
  needsTicker,
  (need) => {
    if (need && ticker === undefined) {
      now.value = Date.now();
      ticker = setInterval(() => {
        now.value = Date.now();
      }, 1000);
    } else if (!need) {
      stopTicker();
    }
  },
  { immediate: true },
);

// ---- 모바일 모달(UXD-02) ----
const compactQuery =
  typeof window.matchMedia === "function"
    ? window.matchMedia("(max-width: 760px)")
    : null;
const compact = ref(compactQuery?.matches ?? false);
/** 화면을 덮는 모바일 패널 — 포커스를 가두고 본문 스크롤을 잠근다. */
const modal = computed(() => open.value && compact.value);
/** 상단바 버튼으로 서는가(좁은 폭은 `topbar` 여도 우하단 버튼). */
const inTopbar = computed(() => props.placement === "topbar" && !compact.value);
let releaseScroll: (() => void) | null = null;

/**
 * 760px 경계를 넘으면 런처가 상단바 ↔ `#app` 으로 옮겨지며 DOM 이동으로 포커스가 body 로 떨어진다(cr-fe-common LOW).
 * 런처에 있던 포커스는 옮겨진 런처로 돌린다 — 패널이 열린 채 좁아지면 런처가 숨고 패널이 모달이 되므로 입력칸으로.
 */
function onCompactChange(event: MediaQueryListEvent): void {
  const launcherFocused =
    !!launcher.value && document.activeElement === launcher.value;
  compact.value = event.matches;
  if (!launcherFocused) return;
  if (modal.value) focusInput();
  else void nextTick(() => launcher.value?.focus({ preventScroll: true }));
}

watch(modal, (on) => {
  if (on) {
    releaseScroll ??= lockBodyScroll();
  } else {
    releaseScroll?.();
    releaseScroll = null;
  }
});

// ---- 스크롤 ----
function isNearBottom(): boolean {
  const el = log.value;
  return (
    !el || el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX
  );
}

function scrollToEnd(): void {
  newAnswerBelow.value = false;
  void nextTick(() => {
    const el = log.value;
    if (el) el.scrollTop = el.scrollHeight;
  });
}

function onLogScroll(): void {
  if (newAnswerBelow.value && isNearBottom()) newAnswerBelow.value = false;
}

function focusInput(): void {
  void nextTick(() => input.value?.focus());
}

function openPanel(): void {
  open.value = true;
  unread.value = false;
  if (!launcherSeen.value) {
    launcherSeen.value = true;
    try {
      sessionStorage.setItem(LAUNCHER_SEEN_KEY, "1");
    } catch {
      // 저장소가 막힌 브라우저는 이번 화면에서만 접는다.
    }
  }
  scrollToEnd();
  focusInput();
}

/** 상단바 버튼은 열린 동안에도 보이므로 열기 · 최소화를 오간다. */
function onLauncherClick(): void {
  if (open.value) minimize();
  else openPanel();
}

/** 패널이 열린 동안 상단바 버튼에서 누른 ESC 도 최소화한다(포커스는 버튼에 그대로 — 패널 안 ESC 와 같은 도착점). */
function onLauncherKeydown(event: KeyboardEvent): void {
  if (
    event.key !== "Escape" ||
    event.isComposing ||
    !open.value ||
    hasOpenModal()
  ) {
    return;
  }
  event.stopPropagation();
  minimize();
}

function minimize(): void {
  const active = document.activeElement;
  const focusInside = active instanceof Node && !!panel.value?.contains(active);
  open.value = false;
  // 패널 안에서 최소화했을 때만 버튼으로 되돌린다(본문을 조작하던 포커스는 그 자리에 둔다).
  if (focusInside) void nextTick(() => launcher.value?.focus());
}

/** 진행 중 질문 요청을 끊는다(답은 `epoch` 로 버린다). 서버가 이미 받은 요청은 끝까지 돌아 호출 횟수를 셀 수 있다. */
function abortAsk(): void {
  askController?.abort();
  askController = null;
}

/** 대화 · 안내 · 잠금을 비운다(입력칸은 그대로). 진행 중 요청은 끊고 답은 버린다. */
function resetConversation(): void {
  epoch += 1;
  abortAsk();
  messages.value = [];
  error.value = null;
  loading.value = false;
  unread.value = false;
  dailyLimited.value = false;
  newAnswerBelow.value = false;
  copiedIndex.value = null;
  clearedMessages.value = null;
}

/** 지운 대화를 되살린다. 답을 받지 못한 마지막 질문은 대화로 되살리지 않고 입력칸에 돌려놓는다(같은 질문이 연달아 쌓이지 않게). */
function restoreConversation(snapshot: AssistantMessage[]): void {
  const restored = snapshot.slice();
  const pending = restored.at(-1)?.role === "user" ? restored.pop() : undefined;
  messages.value = restored;
  if (pending && draft.value.trim() === "") draft.value = pending.content;
  scrollToEnd();
}

/**
 * 「대화 지우기」 — 확인창 대신 되돌리기를 준다. 데스크톱은 되돌리기 토스트, 모바일 모달은 Tab 이 패널 밖 토스트에 닿지 못하므로
 * 패널 안 되돌리기 줄로 둔다(새 질문 · 회사 전환 · 다시 지우기 전까지).
 */
function clearConversation(): void {
  const snapshot = messages.value.slice();
  const workspaceId = workspace.value.id;
  resetConversation();
  focusInput();
  if (snapshot.length === 0) return;
  if (modal.value) {
    clearedMessages.value = snapshot;
    return;
  }
  toastUndo("대화를 지웠습니다.", () => {
    // 회사를 바꿨거나 새 대화를 시작했으면 되살리지 않는다(다른 회사 맥락 · 새 질문을 덮지 않게).
    if (
      workspace.value.id !== workspaceId ||
      messages.value.length > 0 ||
      loading.value
    ) {
      toastInfo("새 대화가 시작되어 지운 대화를 되살리지 않았습니다.");
      return;
    }
    restoreConversation(snapshot);
  });
}

/** 패널 안 되돌리기 줄(모바일 모달) */
function undoClear(): void {
  const snapshot = clearedMessages.value;
  if (!snapshot) return;
  clearedMessages.value = null;
  restoreConversation(snapshot);
  focusInput();
}

/** 라우트 전환 · 워크스페이스 전환으로 끊긴 요청인가. 네트워크 오류도 status 0 이지만 신호가 abort 되지 않았다. */
function isCanceled(response: ApiResponse<unknown>): boolean {
  const config = response.config as InternalAxiosRequestConfig | undefined;
  return response.status === 0 && config?.signal?.aborted === true;
}

function codeOf(response: ApiResponse<unknown>): string | undefined {
  const code = (response.data as { code?: unknown } | undefined)?.code;
  return typeof code === "string" ? code : undefined;
}

/** 하루 상한 초과(사용자 · 회사 전체) — 오늘은 다시 보내도 거절된다. 도우미 상한 · AI 생성 상한 모두. */
function isDailyLimit(response: ApiResponse<unknown>): boolean {
  return (
    response.status === 429 &&
    (codeOf(response)?.endsWith("_DAILY_LIMIT_EXCEEDED") ?? false)
  );
}

/**
 * 분당 상한 429 에 서버가 알려 준 대기 시간이 있으면 카운트다운 안내로 바꾼다. 서버 문구(「…1분 뒤 다시 질문해 주세요」)에 남은 초를
 * 이어 붙이면 대기 시간이 서로 다른 문장이 되기 때문이다. 대기 시간이 없으면 서버 문구 그대로 둔다.
 */
function withRetryAfter(
  notice: AssistantNotice,
  response: ApiResponse<AssistantAskResponse>,
): AssistantNotice {
  const code = codeOf(response);
  if (response.status !== 429 || !code?.endsWith("_MINUTE_LIMIT_EXCEEDED")) {
    return notice;
  }
  const seconds = retryAfterSeconds(response);
  if (seconds === null) return { ...notice, tone: "warn" };
  return {
    ...notice,
    tone: "warn",
    retryAt: Date.now() + seconds * 1000,
    countdown: code.startsWith("ASSISTANT_")
      ? (left) =>
          `질문이 너무 잦습니다. ${String(left)}초 뒤 다시 질문해 주세요.`
      : (left) =>
          `AI 생성 요청이 너무 잦습니다. ${String(left)}초 뒤 다시 시도해 주세요.`,
  };
}

function failureOf(
  response: ApiResponse<AssistantAskResponse>,
): AssistantNotice {
  if (isCanceled(response)) {
    return {
      message:
        "화면을 옮기면서 답변 요청이 취소되었습니다. 다시 시도해 주세요.",
      retryable: true,
      tone: "warn",
    };
  }
  // 네트워크 끊김은 인터셉터가 이미 토스트로 알렸다(status 0 · 신호 중단 없음). 패널에는 짧게만 둔다.
  if (response.status === 0) {
    return { message: "연결이 끊겼습니다.", retryable: true, tone: "danger" };
  }
  const daily = isDailyLimit(response);
  // AI 생성 실패(503 `LLM_*`)는 서버 원인 문구(크레딧 소진 · 미연결 등)를 그대로 쓴다.
  const llmMessage = llmFailureMessage(response);
  if (llmMessage) {
    return withRetryAfter(
      {
        message: llmMessage,
        retryable: !daily && !isLlmInputRejectedFailure(response),
        tone: daily ? "warn" : "danger",
      },
      response,
    );
  }
  const body = response.data as
    { code?: unknown; message?: unknown } | undefined;
  const serverMessage = body?.message;
  return withRetryAfter(
    {
      // 입력 상한 위반(422)은 영문 pydantic 원문 대신 어느 칸 · 무엇이 넘었는지 한국어로(72차 CW72-05).
      message:
        response.status === 422
          ? validationFailureText(response, VALIDATION_FIELD_LABELS)
          : typeof serverMessage === "string" && serverMessage !== ""
            ? serverMessage
            : "답변을 받지 못했습니다. 잠시 뒤 다시 시도해 주세요.",
      // 입력 상한 위반(422) · 대화 순서 오류는 같은 요청을 다시 보내도 같다. 빈 답(`ASSISTANT_EMPTY_ANSWER`)은 다시 물을 수 있다.
      // 호출 상한(429)은 서버 문구를 그대로 보이고 질문은 대화에 남긴다. 분당 상한만 카운트다운 뒤 「다시 시도」로 보낼 수 있고,
      // 하루 상한(사용자 · 회사 전체)은 오늘 다시 보내도 같아 전송을 잠근다.
      retryable:
        response.status !== 422 &&
        body?.code !== "ASSISTANT_LAST_MESSAGE_NOT_USER" &&
        !daily,
      // 상한은 실패가 아니라 기다림이다(warn). 그 밖의 거절은 실패(danger).
      tone: daily ? "warn" : "danger",
    },
    response,
  );
}

async function ask(): Promise<void> {
  const requestEpoch = epoch;
  abortAsk();
  const controller = new AbortController();
  askController = controller;
  loading.value = true;
  loadingStartedAt = Date.now();
  error.value = null;
  scrollToEnd();
  try {
    const response = await postApi<AssistantAskRequest, AssistantAskResponse>(
      "api/v1/assistant/ask",
      {
        pageId: pageId.value || undefined,
        pageTitle: pageTitle.value.slice(0, PAGE_TITLE_LIMIT),
        // 질문을 보내는 순간의 화면 내용을 읽는다(끄면 보내지 않는다).
        pageContext: sharePageContext.value
          ? (getAssistantPageContext() ?? undefined)
          : undefined,
        // 서버는 메시지마다 4000자 상한(422)이라 긴 AI 답도 잘라 보낸다.
        messages: messages.value.slice(-HISTORY_LIMIT).map((message) => ({
          role: message.role,
          content: message.content.slice(0, MAX_LENGTH),
        })),
      },
      // `readLike`: 역할 4종이 모두 허용하는 읽기 성격 POST — 재발급이 역할 강등을 드러내도 취소하지 않는다(90차 C90-25 · AU90-07)
      {
        successAlert: false,
        failureAlert: false,
        signal: controller.signal,
        readLike: true,
      },
    );
    if (requestEpoch !== epoch) return;
    loading.value = false;
    const answer = response.data?.answer;
    if (response.success && typeof answer === "string" && answer !== "") {
      // 답이 붙기 전에 잰다(붙은 뒤에는 늘 맨 아래가 아니다).
      const follow = isNearBottom();
      messages.value.push({ role: "assistant", content: answer });
      if (!open.value) unread.value = true;
      if (follow || !open.value) scrollToEnd();
      else newAnswerBelow.value = true;
    } else {
      if (isDailyLimit(response)) dailyLimited.value = true;
      error.value = failureOf(response);
      scrollToEnd();
    }
  } catch (e) {
    if (requestEpoch !== epoch) return;
    loading.value = false;
    // 질문은 저장하는 동작이 아니라 결과 불명이어도 다시 묻기만 하면 된다.
    error.value = {
      message: isUnconfirmedOutcome(e)
        ? "답변이 늦어 응답 시간이 초과되었습니다. 질문을 짧게 나눠 다시 시도해 주세요."
        : "일시적인 오류가 발생했습니다. 잠시 뒤 다시 시도해 주세요.",
      retryable: true,
      tone: "danger",
    };
    scrollToEnd();
  } finally {
    if (askController === controller) askController = null;
  }
}

function send(text: string): void {
  const content = text.trim();
  if (content === "" || loading.value || dailyLimited.value) return;
  // 실패한 질문 뒤에 새로 물으면 실패한 질문은 버린다(사용자 질문이 연달아 쌓이지 않게).
  if (error.value && messages.value.at(-1)?.role === "user") {
    messages.value.pop();
  }
  messages.value.push({ role: "user", content: content.slice(0, MAX_LENGTH) });
  draft.value = "";
  clearedMessages.value = null;
  void ask();
}

function retry(): void {
  if (
    loading.value ||
    dailyLimited.value ||
    retryRemaining.value > 0 ||
    messages.value.at(-1)?.role !== "user"
  ) {
    return;
  }
  void ask();
}

/**
 * 「기다리지 않기」 — 요청을 끊고(`signal`) 마지막 질문을 남겨 「다시 시도」 를 보인다. 브라우저 쪽 연결만 끊으므로 서버가 이미
 * 받은 요청은 끝까지 돌아 호출 횟수를 셀 수 있다. 끊긴 응답은 `epoch` 로 버린다(화면 이동 취소 안내를 띄우지 않는다).
 */
function stopWaiting(): void {
  if (!loading.value || Date.now() - loadingStartedAt < STOP_GUARD_MS) return;
  epoch += 1;
  abortAsk();
  loading.value = false;
  error.value = {
    message:
      "답변을 기다리지 않기로 했습니다. 다시 시도하거나 새로 질문해 주세요.",
    retryable: true,
    tone: "neutral",
  };
  scrollToEnd();
  focusInput();
}

/** 전송 버튼 하나가 대기 중에는 「기다리지 않기」 가 된다(버튼을 바꿔 끼우면 누르던 포커스를 잃는다). */
function onActionClick(): void {
  if (loading.value) stopWaiting();
}

/** 조합이 막 끝났는가. 사파리는 확정 Enter 의 keydown 을 compositionend 뒤에 isComposing=false 로 보낸다. */
let composingEnded = false;

function onCompositionEnd(): void {
  composingEnded = true;
  setTimeout(() => {
    composingEnded = false;
  });
}

function onInputKeydown(event: KeyboardEvent): void {
  if (event.key !== "Enter" || event.shiftKey) return;
  // 한글 조합 중 Enter 는 글자 확정이라 보내지 않는다.
  if (event.isComposing || composingEnded) return;
  event.preventDefault();
  send(draft.value);
}

function onPanelKeydown(event: KeyboardEvent): void {
  // 위에 확인창이 떠 있으면 그 층이 키보드를 처리한다.
  if (hasOpenModal()) return;
  if (event.key === "Tab") {
    if (modal.value && panel.value) trapTabKey(event, panel.value);
    return;
  }
  // 한글 조합 중 ESC 는 조합 취소다.
  if (event.key !== "Escape" || event.isComposing) return;
  event.stopPropagation();
  minimize();
}

async function copy(content: string, index: number): Promise<void> {
  try {
    await navigator.clipboard.writeText(content);
    copiedIndex.value = index;
    if (copiedTimer !== undefined) clearTimeout(copiedTimer);
    copiedTimer = setTimeout(() => {
      copiedIndex.value = null;
    }, 2000);
  } catch {
    toastError("복사하지 못했습니다. 답변을 직접 선택해 복사해 주세요.");
  }
}

watch(
  () => workspace.value.id,
  (id, previous) => {
    if (id === previous) return;
    resetConversation();
    // 보내지 않은 질문도 비운다 — 다른 회사 문장이 새 회사 문맥과 함께 나가지 않게(CO70-05).
    draft.value = "";
  },
);

// 화면이 도우미로 대화를 넘긴다(AI 검토 「도우미에게 이어 묻기」, UXD-28) — 열고 초안만 채운다.
watch(
  () => assistantOpenRequest()?.seq,
  () => {
    const request = assistantOpenRequest();
    if (!request) return;
    const text = request.draft.trim();
    if (text !== "") {
      const current = draft.value.trim();
      draft.value = (
        current === "" || current === text ? text : `${current}\n\n${text}`
      ).slice(0, MAX_LENGTH);
    }
    openPanel();
  },
);

onMounted(() => {
  compactQuery?.addEventListener("change", onCompactChange);
});

onBeforeUnmount(() => {
  compactQuery?.removeEventListener("change", onCompactChange);
  releaseScroll?.();
  releaseScroll = null;
  stopTicker();
  abortAsk();
  if (copiedTimer !== undefined) clearTimeout(copiedTimer);
});
</script>

<template>
  <!-- 상단바 버튼은 놓인 자리에 그대로, 우하단 버튼은 `#app` 으로 옮겨 그린다(같은 버튼이라 포커스 복귀 · 안 읽음 표지가 이어진다). -->
  <Teleport to="#app" :disabled="inTopbar">
    <button
      v-show="inTopbar || !open"
      ref="launcher"
      type="button"
      class="ai-assistant-launcher"
      :class="
        inTopbar ? 'is-topbar' : ['is-fab', { 'is-extended': !launcherSeen }]
      "
      :aria-label="launcherLabel"
      aria-haspopup="dialog"
      :aria-controls="panelId"
      :aria-expanded="open"
      @click="onLauncherClick"
      @keydown="onLauncherKeydown"
    >
      <UiIcon name="sparkle" :size="inTopbar ? 16 : 20" />
      <span class="ai-assistant-launcher-text" aria-hidden="true"
        >AI 도우미</span
      >
      <span
        v-if="unread"
        class="ai-assistant-launcher-dot"
        aria-hidden="true"
      />
    </button>
  </Teleport>

  <Teleport to="#app">
    <Transition name="ai-assistant-pop">
      <section
        v-show="open"
        :id="panelId"
        ref="panel"
        class="ai-assistant"
        :class="{
          'is-topbar': inTopbar,
          'is-empty-chat': messages.length === 0,
        }"
        role="dialog"
        :aria-modal="modal ? 'true' : 'false'"
        :aria-labelledby="titleId"
        tabindex="-1"
        @keydown="onPanelKeydown"
      >
        <header class="ai-assistant-head">
          <div class="ai-assistant-heading">
            <h2 :id="titleId" class="ai-assistant-title">
              <UiIcon name="sparkle" :size="16" />
              AI 도우미
            </h2>
            <span class="ai-assistant-page">{{ pageTitle }}</span>
          </div>
          <!-- 73차 UXM-03: 지우기는 파괴 동작이라 접기(⌄)와 무게를 가른다(가리키면 빨강). 지울 대화가 없으면 그리지 않는다 — 누르면 입력칸으로
               포커스가 옮겨 가므로 사라져도 포커스가 떨어지지 않는다. -->
          <IconButton
            v-if="messages.length > 0 || error"
            icon="trash"
            label="대화 지우기"
            variant="danger-quiet"
            @click="clearConversation"
          />
          <IconButton
            icon="chevron-down"
            label="최소화"
            variant="quiet"
            @click="minimize"
          />
        </header>

        <div
          ref="log"
          class="ai-assistant-body"
          :class="{ 'is-empty': messages.length === 0 }"
          @scroll.passive="onLogScroll"
        >
          <!-- 73차 UXM-02(ux-m-common §2-11): 빈 대화는 입력칸 바로 위에 모은다(위에 두면 제안 아래 ~300px 이 비었다). 제안은 거르기 알약
               (FilterChips)과 같은 모양이라 "거르기" 로 읽혀 선 있는 작은 버튼(ghost sm · AI 글색)으로 둔다. -->
          <div v-if="messages.length === 0" class="ai-assistant-empty">
            <EmptyState
              icon="sparkle"
              title="이 화면에 대해 물어보세요"
              description="입력 방법 · 예시 · 다음 단계를 안내합니다."
            />
            <div class="ai-assistant-chips">
              <LoadingButton
                v-for="question in suggestions"
                :key="question"
                class="ui-ai ai-assistant-chip"
                variant="ghost"
                size="sm"
                :disabled="loading || dailyLimited"
                @click="send(question)"
              >
                {{ question }}
              </LoadingButton>
            </div>
            <p class="ai-assistant-trust">
              <UiIcon name="lock" :size="14" />
              대화는 저장되지 않습니다 · 새로고침하거나 회사를 바꾸면 지워집니다
            </p>
          </div>

          <ol class="ai-assistant-log" role="log" aria-live="polite">
            <li
              v-for="(message, index) in messages"
              :key="index"
              class="ai-assistant-msg"
              :class="`is-${message.role}`"
            >
              <span class="ui-sr-only">{{
                message.role === "user" ? "내 질문" : "AI 답변"
              }}</span>
              <p v-if="message.role === 'user'" class="ai-assistant-bubble">
                {{ message.content }}
              </p>
              <template v-else>
                <div class="ai-assistant-bubble">
                  <AiText :text="message.content" size="sm" />
                </div>
                <div class="ai-assistant-msg-tools">
                  <IconButton
                    :icon="copiedIndex === index ? 'check' : 'copy'"
                    label="답변 복사"
                    variant="quiet"
                    size="sm"
                    @click="copy(message.content, index)"
                  />
                  <span v-if="copiedIndex === index" class="ai-assistant-copied"
                    >복사됨</span
                  >
                </div>
              </template>
            </li>
            <li
              v-if="loading"
              class="ai-assistant-msg is-assistant is-pending"
              aria-busy="true"
            >
              <p class="ai-assistant-bubble">
                <span class="ai-assistant-dots" aria-hidden="true">
                  <span /><span /><span />
                </span>
                <span
                  >답변을 만드는 중<span
                    v-if="pendingSeconds >= ELAPSED_FROM_SECONDS"
                    aria-hidden="true"
                  >
                    · {{ pendingSeconds }}초</span
                  ></span
                >
              </p>
            </li>
          </ol>

          <div v-if="showFollowUps" class="ai-assistant-chips is-follow">
            <LoadingButton
              v-for="followUp in FOLLOW_UPS"
              :key="followUp.label"
              class="ui-ai ai-assistant-chip"
              variant="ghost"
              size="sm"
              :title="followUp.question"
              @click="send(followUp.question)"
            >
              {{ followUp.label }}
            </LoadingButton>
          </div>

          <Callout v-if="clearedMessages" tone="neutral" role="status">
            대화를 지웠습니다.
            <template #actions>
              <LoadingButton variant="ghost" size="sm" @click="undoClear">
                되돌리기
              </LoadingButton>
            </template>
          </Callout>

          <Callout v-if="error" :tone="error.tone" role="alert">
            {{ noticeMessage }}
            <template
              v-if="
                error.retryable &&
                retryRemaining === 0 &&
                messages.at(-1)?.role === 'user'
              "
              #actions
            >
              <LoadingButton variant="ghost" size="sm" @click="retry">
                다시 시도
              </LoadingButton>
            </template>
          </Callout>

          <!-- 위로 스크롤해 읽는 중 도착한 답(73차 UXA73-51 — 손 버튼 → 표준 ghost sm). 글 위에 떠 있어 면이 있는 ghost 를 쓴다(quiet 는 선 · 면이 없다). -->
          <LoadingButton
            v-if="newAnswerBelow"
            class="ai-assistant-jump"
            variant="ghost"
            size="sm"
            icon="arrow-down"
            @click="scrollToEnd"
          >
            새 답변
          </LoadingButton>
        </div>

        <form
          class="ai-assistant-foot"
          :class="{ 'is-locked': !!sendBlockedReason }"
          @submit.prevent="send(draft)"
        >
          <!-- 누르는 즉시 켜짐/꺼짐인 설정 = `UiSwitch`(73차 UXA73-51 — 칩 모양은 FilterChips 로 읽혔다). 꺼짐 설명은 스위치 아래 줄. -->
          <div v-if="pageContextAvailable" class="ai-assistant-context">
            <UiSwitch
              v-model="sharePageContext"
              :label="`${pageTitle} 작성 내용 포함`"
              :description="
                sharePageContext ? undefined : '작성 내용 없이 질문합니다'
              "
            />
            <Disclosure
              v-if="sharePageContext"
              v-model:open="contextPreviewOpen"
              class="ai-assistant-context-peek"
              variant="plain"
              title="보내는 내용 보기"
              :summary-meta="`총 ${pageContextLength.toLocaleString('ko-KR')}자`"
            >
              <template #summary>
                <span class="ai-assistant-context-peek-title"
                  >보내는 내용 보기</span
                >
              </template>
              <p class="ai-assistant-context-preview">
                {{ pageContextPreview }}
              </p>
            </Disclosure>
          </div>
          <label class="ui-sr-only" :for="`${panelId}-input`">질문 입력</label>
          <AutoGrowTextarea
            :id="`${panelId}-input`"
            ref="input"
            v-model="draft"
            :maxlength="MAX_LENGTH"
            :min-rows="2"
            :max-rows="6"
            placeholder="질문을 입력하세요"
            :aria-describedby="`${panelId}-keys`"
            @keydown="onInputKeydown"
            @compositionend="onCompositionEnd"
          />
          <LoadingButton
            :type="loading ? 'button' : 'submit'"
            :variant="loading ? 'ghost' : 'primary'"
            size="sm"
            :disabled="!loading && !canSend"
            :blocked-reason="sendBlockedReason"
            @click="onActionClick"
          >
            {{ loading ? "기다리지 않기" : "전송" }}
          </LoadingButton>
          <!-- 78차 VA78-21 · fix1 RA78-16: 단축키 안내는 placeholder(두 줄로 꺾임) 대신 입력칸 아래 한 줄 — 첫 질문 뒤에도 남고 입력칸 접근 설명이 된다. -->
          <p :id="`${panelId}-keys`" class="ai-assistant-keys">
            Enter 전송 · Shift+Enter 줄바꿈
          </p>
        </form>
      </section>
    </Transition>
  </Teleport>
</template>

<style scoped>
/*
 * 위치: 하단 고정 버튼 줄(StickyActionBar)이 `:root` 에 적는 `--ui-action-bar-offset`(줄 바닥 거리 + 높이 + 12) 만큼 띄우고,
 * 80차(UXC80-04): 줄 높이만 알리는 `--ui-sticky-bar-h` + 12 보다는 아래로 내려가지 않는다(줄이 없으면 둘 다 0 이라 기본 20px),
 * 오른쪽 비모달 시트(SidePanel)가 적는 `--ui-dock-right` 만큼 비켜선다. 겹침은 비모달 시트(59) 아래 `--ui-z-assistant`(58).
 * 상단바 자리(`is-topbar`)의 패널은 위를 상단바 아래에 붙이고 아래 끝은 같은 값으로 버튼 줄 위에서 멈춘다.
 */
.ai-assistant-launcher,
.ai-assistant {
  --ai-assistant-gap: 20px;
  --ai-assistant-bottom: max(
    var(--ai-assistant-gap),
    var(--ui-action-bar-offset, 0px),
    calc(var(--ui-sticky-bar-h, 0px) + 12px)
  );
  --ai-assistant-right: calc(
    var(--ai-assistant-gap) + var(--ui-dock-right, 0px)
  );
}

.ai-assistant-launcher.is-fab {
  position: fixed;
  right: var(--ai-assistant-right);
  bottom: var(--ai-assistant-bottom);
  z-index: var(--ui-z-assistant);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 52px;
  height: 52px;
  padding: 0;
  border: 0;
  border-radius: var(--ui-radius-pill);
  background: var(--ui-ai);
  box-shadow: var(--ui-shadow-3);
  color: var(--ui-surface);
  cursor: pointer;
  /*
   * 76차 UXA76-17: 하단 줄 · 비모달 시트가 자리 값을 바꿀 때 런처가 한 프레임에 70~110px 순간이동하지 않게 bottom · right 도 짧게 옮긴다
   * (하단 줄이 미러로 내려간 동안의 오프셋 유지는 StickyActionBar 몫 — 76차 ds-a). 동작 줄이기 설정이면 옮김 없이 바로.
   */
  transition:
    transform var(--ui-dur-fast) var(--ui-ease),
    bottom var(--ui-dur-fast) var(--ui-ease),
    right var(--ui-dur-fast) var(--ui-ease);
}

.ai-assistant-launcher.is-fab:hover {
  transform: translateY(-2px);
}

.ai-assistant-launcher:focus-visible {
  outline: var(--ui-focus-outline);
  outline-offset: 3px;
}

/* 글자 알약은 넓은 화면(≥981)에서 처음 한 번만 — 연 뒤에는 원으로 접는다. */
.ai-assistant-launcher-text {
  display: none;
  font-size: var(--ui-fs-sm);
  font-weight: 800;
  white-space: nowrap;
}

@media (min-width: 981px) {
  .ai-assistant-launcher.is-extended {
    padding: 0 20px 0 16px;
  }

  .ai-assistant-launcher.is-extended .ai-assistant-launcher-text {
    display: inline;
  }
}

/*
 * 상단바 버튼(UXG-02) — 행 안 ghost 버튼(`btn ghost ui-sm`)과 같은 치수 · 선(36 · 반경 12 · --ui-input-line — 89차 C89-14 컨트롤 선 하나)에 AI 색 글 · 아이콘
 * (73차 UXM-04 — 아이콘만 보라 · 글은 파랑이라 한 버튼 안 두 색이었다).
 * `.btn.ghost` 를 달지 않는다 — 프로토타입 `.btn.ghost{…!important}` 가 펼침 상태 면을 덮어 `!important` 를 늘려야 했다.
 * 패널이 열린 동안은 AI 면 · 선으로 눌린 상태를 보인다(상태는 `aria-expanded` 로도 읽힌다). 글자는 늘 보인다(원으로 접지 않는다).
 */
.ai-assistant-launcher.is-topbar {
  position: relative;
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid var(--ui-input-line);
  border-radius: var(--ui-radius-control);
  background: var(--ui-surface);
  color: var(--ui-ai);
  cursor: pointer;
  transition:
    background-color var(--ui-dur-fast) var(--ui-ease),
    border-color var(--ui-dur-fast) var(--ui-ease),
    color var(--ui-dur-fast) var(--ui-ease);
}

.ai-assistant-launcher.is-topbar:hover {
  border-color: var(--ui-ai-line);
  background: var(--ui-surface-2);
}

.ai-assistant-launcher.is-topbar:focus-visible {
  outline: var(--ui-focus-outline);
  outline-offset: 2px;
}

.ai-assistant-launcher.is-topbar .ai-assistant-launcher-text {
  display: inline;
}

.ai-assistant-launcher.is-topbar[aria-expanded="true"] {
  border-color: var(--ui-ai-line);
  background: var(--ui-ai-bg);
  color: var(--ui-ai);
}

/* 안 읽은 답 표지 — 오류(빨강)가 아니라 AI 색 점(흰 테두리로 버튼 면과 가른다) */
.ai-assistant-launcher-dot {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 12px;
  height: 12px;
  border: 2px solid var(--ui-surface);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-ai);
}

.ai-assistant-launcher.is-topbar .ai-assistant-launcher-dot {
  top: -4px;
  right: -4px;
}

.ai-assistant {
  position: fixed;
  right: var(--ai-assistant-right);
  bottom: var(--ai-assistant-bottom);
  z-index: var(--ui-z-assistant);
  display: flex;
  flex-direction: column;
  width: min(
    400px,
    calc(100vw - var(--ai-assistant-gap) * 2 - var(--ui-dock-right, 0px))
  );
  height: min(
    620px,
    calc(100dvh - var(--ui-topbar-h) - 20px - var(--ai-assistant-bottom))
  );
  border: 1px solid var(--ui-line);
  border-radius: var(--ui-radius-card);
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-3);
  color: var(--ui-text);
  overflow: hidden;
}

/*
 * 빈 대화는 높이를 내용만큼(81차 UXA81-23 · a-C12) — 620 고정이면 제안 3개가 아래에 모이고 위 240px 가 비어 본문을 가렸다. 최대 높이는 같은 상한(화면 안)이고,
 * 첫 메시지가 생기면 기본 620(화면 상한)으로 늘어난다. 높이 전환은 `auto` 로는 애니메이션이 안 되어 두지 않는다.
 */
.ai-assistant.is-empty-chat {
  height: auto;
  max-height: min(
    620px,
    calc(100dvh - var(--ui-topbar-h) - 20px - var(--ai-assistant-bottom))
  );
}

.ai-assistant.is-topbar.is-empty-chat {
  height: auto;
  max-height: min(
    620px,
    calc(100dvh - var(--ui-topbar-h) - 12px - var(--ai-assistant-bottom))
  );
}

.ai-assistant:focus {
  outline: none;
}

/* 상단바 버튼 아래 오른쪽에 뜬다 — 버튼에서 패널이 내려오는 방향과 맞춘다. */
.ai-assistant.is-topbar {
  top: calc(var(--ui-topbar-h) + 12px);
  bottom: auto;
  height: min(
    620px,
    calc(100dvh - var(--ui-topbar-h) - 12px - var(--ai-assistant-bottom))
  );
}

.ai-assistant-head {
  display: flex;
  flex: none;
  align-items: center;
  gap: 2px;
  padding: 12px 10px 12px 16px;
  border-bottom: 1px solid var(--ui-line);
  background: var(--ui-ai-bg);
}

.ai-assistant-heading {
  display: grid;
  flex: 1 1 auto;
  gap: 2px;
  min-width: 0;
}

.ai-assistant-title {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  color: var(--ui-ai);
  font-size: var(--ui-fs-md);
  font-weight: 900;
  line-height: 1.35;
}

.ai-assistant-page {
  overflow: hidden;
  color: var(--ui-text-3);
  font-size: var(--ui-fs-xs);
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ai-assistant-body {
  display: grid;
  flex: 1 1 auto;
  align-content: start;
  gap: var(--ui-gap-md);
  min-height: 0;
  padding: 16px;
  overflow-y: auto;
  overscroll-behavior: contain;
}

/* 빈 대화는 본문 아래쪽(입력칸 바로 위)에 모은다(73차 UXM-02) — 위에 두면 제안 아래 ~300px 이 비었다. */
.ai-assistant-body.is-empty {
  align-content: end;
}

.ai-assistant-empty {
  display: grid;
  gap: var(--ui-gap-sm);
}

.ai-assistant-empty :deep(.ui-empty-title > .ui-icon) {
  color: var(--ui-ai);
}

.ai-assistant-empty p {
  margin: 0;
  color: var(--ui-text-2);
  font-size: var(--ui-fs-sm);
  line-height: 1.6;
  word-break: keep-all;
}

.ai-assistant-empty .ai-assistant-trust {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  color: var(--ui-text-3);
  font-size: var(--ui-fs-xs);
  font-weight: 700;
  line-height: 1.5;
}

/* 입력칸 아래 단축키 한 줄(78차 VA78-21 · fix1 RA78-16 — 빈 대화에서만 보이던 것을 늘 둔다) */
.ai-assistant-keys {
  grid-column: 1 / -1;
  margin: 0;
  color: var(--ui-text-3);
  font-size: var(--ui-fs-xs);
  font-weight: 700;
}

.ai-assistant-trust > .ui-icon {
  flex: none;
  margin-top: 2px;
}

.ai-assistant-chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--ui-gap-xs);
}

/* 제안은 LoadingButton ghost sm(`btn ghost ui-sm ui-ai` — 36 · 반경 12 · AI 글색, 73차 UXM-02). 긴 제안은 버튼 안에서 꺾는다. */
.ai-assistant-chip {
  max-width: 100%;
  line-height: 1.4;
  text-align: left;
  white-space: normal;
}

.ai-assistant-log {
  display: grid;
  gap: var(--ui-gap-sm);
  margin: 0;
  padding: 0;
  list-style: none;
}

.ai-assistant-msg {
  display: grid;
  justify-items: start;
  gap: 4px;
}

.ai-assistant-msg.is-user {
  justify-items: end;
}

.ai-assistant-bubble {
  max-width: 88%;
  margin: 0;
  padding: 10px 12px;
  border-radius: var(--ui-radius-subbox);
  background: var(--ui-surface-2);
  color: var(--ui-text);
  font-size: var(--ui-fs-sm);
  line-height: 1.6;
  overflow-wrap: anywhere;
}

.ai-assistant-msg.is-user .ai-assistant-bubble {
  background: var(--ui-ai);
  color: var(--ui-surface);
  white-space: pre-wrap;
}

.ai-assistant-msg.is-assistant .ai-assistant-bubble {
  border: 1px solid var(--ui-ai-line);
  background: var(--ui-ai-bg);
}

.ai-assistant-msg.is-pending .ai-assistant-bubble {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--ui-text-3);
}

/* 대기 표지 — 점 셋이 차례로 밝아진다(동작 줄이기면 멈춘 점) */
.ai-assistant-dots {
  display: inline-flex;
  gap: 3px;
}

.ai-assistant-dots > span {
  width: 6px;
  height: 6px;
  border-radius: var(--ui-radius-pill);
  background: var(--ui-ai);
  opacity: 0.3;
  animation: ai-assistant-dot 1.2s var(--ui-ease) infinite;
}

.ai-assistant-dots > span:nth-child(2) {
  animation-delay: 0.2s;
}

.ai-assistant-dots > span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes ai-assistant-dot {
  0%,
  80%,
  100% {
    opacity: 0.3;
  }

  40% {
    opacity: 1;
  }
}

.ai-assistant-msg-tools {
  display: flex;
  align-items: center;
  gap: 4px;
}

.ai-assistant-copied {
  color: var(--ui-text-3);
  font-size: var(--ui-fs-xs);
  font-weight: 700;
}

/* 위로 스크롤해 읽는 중 도착한 답 — 끌어내리지 않고 알린다. 모양은 표준 ghost sm, 여기서는 자리(글 위에 떠 있음)와 그림자만 */
.ai-assistant-jump {
  position: sticky;
  bottom: 0;
  justify-self: center;
  box-shadow: var(--ui-shadow-2) !important;
}

.ai-assistant-foot {
  display: grid;
  flex: none;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: var(--ui-gap-sm);
  padding: 12px 16px;
  border-top: 1px solid var(--ui-line);
  background: var(--ui-surface);
}

/* 하루 상한 뒤 — 잠긴 전송 버튼과 이유 한 줄을 입력칸 아래 한 줄로 */
.ai-assistant-foot.is-locked {
  grid-template-columns: minmax(0, 1fr);
}

/* 입력칸 위 "작성 내용 포함" 스위치 — 회사 내부 문안을 외부 AI 로 보내는 신뢰 표시(UXD-04 → 73차 UXA73-51 `UiSwitch`) */
.ai-assistant-context {
  display: grid;
  grid-column: 1 / -1;
  gap: 6px;
  min-width: 0;
}

/* 패널 바닥은 촘촘한 자리라 스위치 글을 13px · 줄 높이 30 으로(부품 기본 14 · 36). 설명 줄은 부품 그대로 */
.ai-assistant-context :deep(.ui-switch) {
  min-height: 30px;
  font-size: var(--ui-fs-sm);
}

.ai-assistant-context-peek {
  font-size: var(--ui-fs-xs);
}

.ai-assistant-context-peek-title {
  color: var(--ui-text-2);
  font-size: var(--ui-fs-xs);
  font-weight: 700;
}

.ai-assistant-context-preview {
  max-height: 96px;
  margin: 0;
  padding: 8px 10px;
  overflow-y: auto;
  border-radius: var(--ui-radius-subbox);
  background: var(--ui-surface-2);
  color: var(--ui-text-2);
  font-size: var(--ui-fs-xs);
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.ai-assistant-pop-enter-active,
.ai-assistant-pop-leave-active {
  transition:
    opacity var(--ui-dur-fast) var(--ui-ease),
    transform var(--ui-dur-fast) var(--ui-ease);
}

.ai-assistant-pop-enter-from,
.ai-assistant-pop-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

.ai-assistant.is-topbar.ai-assistant-pop-enter-from,
.ai-assistant.is-topbar.ai-assistant-pop-leave-to {
  transform: translateY(-8px);
}

/* 모바일 — 상단바 아래 화면 폭 가득(모달) */
@media (max-width: 760px) {
  .ai-assistant-launcher {
    --ai-assistant-gap: 16px;
  }

  /* 화면 전체를 덮는 패널이라 버튼 줄 위로 띄우지 않는다(최소화하면 버튼 줄이 드러난다). */
  .ai-assistant {
    top: var(--ui-topbar-h);
    right: 0;
    bottom: 0;
    left: 0;
    width: 100vw;
    height: auto;
    border: 0;
    border-top: 1px solid var(--ui-line);
    border-radius: 0;
  }

  /* iOS 홈 표시줄과 입력줄이 겹치지 않게 */
  .ai-assistant-foot {
    padding-bottom: calc(12px + env(safe-area-inset-bottom));
  }
}

/*
 * 78차 VC78-12: 좁은 폭(≤760 — 런처가 우하단 원이 되는 폭)에서 52px 원 + 20px 여백이 카드 오른쪽 끝 버튼(「구성원으로 초대」 · 표 오른쪽 열)을 가렸다.
 * 원은 44(터치 최소) · 여백 12 로 줄여 가리는 면적을 반으로 한다(하단 줄 위 오프셋은 `--ui-action-bar-offset` 이 그대로 더한다).
 */
@media (max-width: 760px) {
  .ai-assistant-launcher,
  .ai-assistant {
    --ai-assistant-gap: 12px;
  }

  .ai-assistant-launcher.is-fab {
    min-width: 44px;
    height: 44px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ai-assistant-launcher.is-fab {
    transition: none;
  }

  .ai-assistant-launcher.is-fab:hover,
  .ai-assistant-pop-enter-from,
  .ai-assistant-pop-leave-to,
  .ai-assistant.is-topbar.ai-assistant-pop-enter-from,
  .ai-assistant.is-topbar.ai-assistant-pop-leave-to {
    transform: none;
  }

  .ai-assistant-dots > span {
    animation: none;
    opacity: 0.6;
  }
}
</style>
